import { createRouter, createWebHistory } from "vue-router";

import MainLayout from "./layouts/MainLayout.vue";

/**
 * Same paths the server-rendered pages used, so existing links and bookmarks
 * keep working. `main` is the stable parent that holds the shell — same
 * convention as the CTF editor router. `meta.title` names the browser tab.
 */
export const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: "/",
      name: "main",
      component: MainLayout,
      children: [
        {
          path: "",
          name: "knowledge",
          meta: { title: "Knowledge" },
          component: () => import("./pages/KnowledgePage.vue"),
        },
        {
          path: "mrs",
          name: "mrs",
          meta: { title: "Merge requests" },
          component: () => import("./pages/MrDashboardPage.vue"),
        },
        {
          path: "mrs/:iid",
          name: "mr-detail",
          component: () => import("./pages/MrDetailPage.vue"),
        },
        {
          path: "sandboxes",
          name: "sandboxes",
          meta: { title: "Sandboxes" },
          component: () => import("./pages/SandboxesPage.vue"),
        },
        {
          path: "releases/:version?",
          name: "releases",
          meta: { title: "Releases" },
          component: () => import("./pages/ReleasePage.vue"),
        },
        {
          path: "sprint",
          name: "sprint",
          meta: { title: "Sprint" },
          component: () => import("./pages/SprintPage.vue"),
        },
        {
          path: "check",
          name: "check",
          meta: { title: "Check" },
          component: () => import("./pages/CheckPage.vue"),
        },
        {
          path: "palace",
          name: "palace",
          meta: { title: "Palace" },
          component: () => import("./pages/PalacePage.vue"),
        },
        {
          path: "settings",
          name: "settings",
          meta: { title: "Settings" },
          component: () => import("./pages/SettingsPage.vue"),
        },
      ],
    },
  ],
});

/**
 * Seven tabs of "CTF Workflow Tool" are indistinguishable in a browser tab
 * strip. The page comes first because the strip truncates from the right.
 */
router.afterEach((to) => {
  // A release manager opens several milestones at once, and every one of those
  // tabs read "Releases".
  const page =
    to.name === "mr-detail"
      ? `!${String(to.params.iid)}`
      : to.name === "releases" && to.params.version
        ? String(to.params.version)
        : typeof to.meta.title === "string"
          ? to.meta.title
          : "";
  document.title = page ? `${page} · CTF Workflow` : "CTF Workflow";

  // A route change tears down the element that was focused, so focus falls to
  // the body and a keyboard reader restarts from the top of the document on
  // every navigation. Move it into the new page instead. `preventScroll`,
  // because the router has already put the page where it belongs.
  requestAnimationFrame(() => {
    const main = document.querySelector("main");
    if (main instanceof HTMLElement) {
      main.setAttribute("tabindex", "-1");
      main.focus({ preventScroll: true });
    }
  });
});
